import { useCallback, useEffect, useState } from 'react';
import { Dialog, Portal, CloseButton, Button, Field, Input, Text, VStack, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AccountService from '@/services/AccountService';
import { toaster } from '@/components/ui/toaster';
import useResendCooldown from '@/auth/useResendCooldown';

const SetupEmailTwoFactorDialog = ({ isOpen, email, onClose, onEnabled }) => {
    const { t } = useTranslation('account');
    const [isSending, setIsSending] = useState(false);
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);
    const resendCooldown = useResendCooldown();
    const { start: startCooldown } = resendCooldown;

    const sendCode = useCallback(async (isResend) => {
        setError('');
        setInfo('');
        setIsSending(true);
        try {
            await AccountService.sendEmailTwoFactorSetupCode();
            if (isResend) setInfo(t('settingsPage.twoFactor.codeResent'));
            startCooldown();
        } catch (err) {
            // 429: a code was just sent and is still valid.
            if (err?.response?.status === 429) startCooldown();
            else setError(t('settingsPage.twoFactor.sendCodeError'));
        } finally {
            setIsSending(false);
        }
    }, [t, startCooldown]);

    useEffect(() => {
        if (!isOpen) return;
        setCode('');
        sendCode(false);
    }, [isOpen, sendCode]);

    const handleConfirm = async () => {
        setError('');
        setInfo('');
        setIsConfirming(true);
        try {
            await AccountService.confirmEmailTwoFactor(code);
            toaster.create({ type: 'success', title: t('settingsPage.twoFactor.toastEnabled') });
            onEnabled();
            onClose();
        } catch (err) {
            setError(err?.response?.status === 400
                ? t('settingsPage.twoFactor.errorInvalidEmailCode')
                : t('settingsPage.twoFactor.toastError'));
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content bg="bg.card" color="white" border="1px solid" borderColor="border.primary" w={{ base: 'calc(100vw - 2rem)', md: 'md' }} maxW="100%">
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight="bold" fontSize="lg" mb={4}>{t('settingsPage.twoFactor.emailSetupTitle')}</Text>

                            {isSending && !resendCooldown.isCoolingDown ? (
                                <VStack py={8}><Spinner color="brand.400" size="lg" /></VStack>
                            ) : (
                                <VStack gap={4} align="stretch">
                                    <Text color="text.grey" fontSize="sm">{t('settingsPage.twoFactor.emailSetupIntro', { email })}</Text>

                                    <Field.Root required>
                                        <Field.Label color="white">{t('settingsPage.twoFactor.emailCodeLabel')} <Field.RequiredIndicator /></Field.Label>
                                        <Input
                                            variant="primary"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                        />
                                    </Field.Root>

                                    {error && <Text color="red.400" fontSize="sm">{error}</Text>}
                                    {info && !error && <Text color="green.300" fontSize="sm">{info}</Text>}

                                    <Button variant="primary" onClick={handleConfirm} loading={isConfirming} disabled={!code}>
                                        {t('settingsPage.twoFactor.confirmButton')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => sendCode(true)}
                                        loading={isSending}
                                        disabled={resendCooldown.isCoolingDown}
                                    >
                                        {resendCooldown.isCoolingDown
                                            ? t('settingsPage.twoFactor.resendCooldown', { seconds: resendCooldown.secondsLeft })
                                            : t('settingsPage.twoFactor.resendCode')}
                                    </Button>
                                </VStack>
                            )}
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default SetupEmailTwoFactorDialog;
